import { Clock3, Globe2, Languages, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAccountRegional } from '@/contexts/AccountRegionalContext'
import {
  CURRENCY_DISPLAY_OPTIONS,
  formatAccountCurrency,
  type CurrencyDisplay,
} from '@/lib/currency'
import { UI_LANGUAGE_OPTIONS, isUiLanguage, type UiLanguage } from '@/lib/uiLanguage'
import {
  formatTimezonePreview,
  timezoneSelectOptions,
} from '@/lib/timezones'
import { SettingsCard } from './SettingsCard'

const selectTriggerClass =
  'h-11 w-full max-w-md rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] focus:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]'

const RegionalSettingsTab = () => {
  const { t, i18n } = useTranslation(['settings', 'common'])
  const {
    currencyDisplay,
    uiLanguage,
    timezone,
    loading,
    saving,
    error,
    updateCurrencyDisplay,
    updateUiLanguage,
    updateTimezone,
  } = useAccountRegional()

  const handleCurrencyChange = async (value: string) => {
    if (value !== 'BRL' && value !== 'USD') return
    if (value === currencyDisplay) return

    const ok = await updateCurrencyDisplay(value as CurrencyDisplay)
    if (ok) {
      toast.success(t('currency.toastSuccess'))
    } else {
      toast.error(t('currency.toastError'))
    }
  }

  const handleLanguageChange = async (value: string) => {
    if (!isUiLanguage(value)) return
    if (value === uiLanguage) return

    const ok = await updateUiLanguage(value as UiLanguage)
    if (ok) {
      toast.success(t('language.toastSuccess'))
    } else {
      toast.error(t('language.toastError'))
    }
  }

  const handleTimezoneChange = async (value: string) => {
    if (!value || value === timezone) return

    const ok = await updateTimezone(value)
    if (ok) {
      toast.success(t('timezone.toastSuccess'))
    } else {
      toast.error(t('timezone.toastError'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">{t('loading')}</span>
      </div>
    )
  }

  // Dev/smoke: key exists only in pt-BR settings — must fall back when UI is en/es.
  const fallbackProbe = t('language.fallbackOnlyPt')
  const timezoneOptions = timezoneSelectOptions(timezone)
  const timezonePreview = formatTimezonePreview(timezone, i18n.language)

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">{t('intro')}</p>

      {error && (
        <div className="rounded-xl border border-red-200 bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626] dark:border-red-500/30 dark:bg-red-500/10 dark:text-[#F87171]">
          {error}
        </div>
      )}

      <SettingsCard className="p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/15 dark:text-[#60A5FA]">
            <Globe2 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t('currency.title')}
            </h3>
            <p className="text-sm text-[#64748B] dark:text-[#A1A1AA] mt-0.5">
              {t('currency.description')}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Label htmlFor="currency-display" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
            {t('currency.label')}
          </Label>
          <Select
            value={currencyDisplay}
            onValueChange={handleCurrencyChange}
            disabled={saving}
          >
            <SelectTrigger id="currency-display" className={selectTriggerClass}>
              <SelectValue placeholder={t('currency.placeholder')} />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {CURRENCY_DISPLAY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(`common:${option.labelKey}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-2 rounded-xl border border-dashed border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 dark:border-[#3F3F46] dark:bg-[#18181B]">
            <p className="text-xs font-medium uppercase tracking-wide text-[#94A3B8] dark:text-[#71717A]">
              {t('currency.previewLabel')}
            </p>
            <p className="mt-1 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {formatAccountCurrency(1234.56, currencyDisplay)}
            </p>
            <p className="mt-1 text-xs text-[#64748B] dark:text-[#A1A1AA]">
              {t('currency.previewHint')}
            </p>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard className="p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/15 dark:text-[#60A5FA]">
            <Languages className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t('language.title')}
            </h3>
            <p className="text-sm text-[#64748B] dark:text-[#A1A1AA] mt-0.5">
              {t('language.description')}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Label htmlFor="language-display" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
            {t('language.label')}
          </Label>
          <Select
            value={uiLanguage}
            onValueChange={handleLanguageChange}
            disabled={saving}
          >
            <SelectTrigger id="language-display" className={selectTriggerClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {UI_LANGUAGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="sr-only" data-testid="i18n-fallback-probe">
            {fallbackProbe}
          </span>
        </div>
      </SettingsCard>

      <SettingsCard className="p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/15 dark:text-[#60A5FA]">
            <Clock3 className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t('timezone.title')}
            </h3>
            <p className="text-sm text-[#64748B] dark:text-[#A1A1AA] mt-0.5">
              {t('timezone.description')}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Label htmlFor="timezone-display" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
            {t('timezone.label')}
          </Label>
          <Select
            value={timezone}
            onValueChange={handleTimezoneChange}
            disabled={saving}
          >
            <SelectTrigger id="timezone-display" className={selectTriggerClass}>
              <SelectValue placeholder={t('timezone.placeholder')} />
            </SelectTrigger>
            <SelectContent className="max-h-[280px] rounded-xl">
              {timezoneOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-2 rounded-xl border border-dashed border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 dark:border-[#3F3F46] dark:bg-[#18181B]">
            <p className="text-xs font-medium uppercase tracking-wide text-[#94A3B8] dark:text-[#71717A]">
              {t('timezone.previewLabel')}
            </p>
            <p className="mt-1 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {timezonePreview}
            </p>
          </div>
        </div>
      </SettingsCard>
    </div>
  )
}

export default RegionalSettingsTab
